import { GENDERS, SCHUFA_DECISION, SCHUFA_RATE_TYPES, SCHUFA_STATE, SCHUFA_STATUS } from 'cpool-constants';
import { z } from 'zod';

/**
 * Consors uses `0` as its "no value" marker for numeric fields. Fed straight into `z.coerce.date()`
 * that becomes `new Date(0)` — the Unix epoch — so an absent date used to surface as `1970-01-01`
 * instead of an empty value. `false` and digit-only strings coerce to bogus dates the same way.
 *
 * A real Consors date is an ISO-ish string (`yyyy-MM-dd`, optionally with a time part), so anything
 * that is not a string or a `Date` is treated as absent, as is a string without date separators.
 * Unparseable and implausibly old values are dropped rather than raised as a validation error: a
 * single bad date must not fail the whole response entry, which would discard every position for
 * that applicant.
 */
const EARLIEST_PLAUSIBLE_YEAR = 1900;

const normaliseConsorsDate = (value: unknown): Date | undefined => {
    if (value === null || value === undefined) return undefined;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    // Numbers and booleans are never real Consors dates, only its "no value" markers.
    if (typeof value !== 'string') return undefined;
    // A real date always carries separators; `0`, `00000000` and friends do not.
    if (!/[-./]/.test(value)) return undefined;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    if (parsed.getUTCFullYear() < EARLIEST_PLAUSIBLE_YEAR) return undefined;
    return parsed;
};

const schemaConsorsIsoDate = z.coerce.date().transform((val: Date) => val.toISOString().slice(0, 10));

/** Consors date that may legitimately be absent — see {@link normaliseConsorsDate}. */
export const schemaConsorsResponseDate = z.preprocess(normaliseConsorsDate, schemaConsorsIsoDate.nullish());

/** As {@link schemaConsorsResponseDate}, but for fields that are declared optional rather than nullish. */
export const schemaConsorsResponseOptionalDate = z.preprocess(normaliseConsorsDate, schemaConsorsIsoDate.optional());

export const schemaSchufaResponsePosition = z.object({
    calculatedInstallment: z.number().nullish(),
    code: z.string().min(1).nullish(),
    creditAmount: z.number().nullish(),
    creditType: z.string().nullish(),
    date: schemaConsorsResponseDate,
    installmentNumber: z.number().nullish(),
    installmentType: z.nativeEnum(SCHUFA_RATE_TYPES).nullish(),
    internalLoan: z.boolean().nullish(),
    meldebetrag: z.number().nullish(),
    outstandingAmount: z.number().nullish(),
});

export const schemaSchufaResponseSchufaKopf = z.object({
    birthDate: schemaConsorsResponseOptionalDate,
    birthPlace: z.string().min(1).optional(),
    currentAddress: z
        .object({
            city: z.string().min(1).optional(),
            postalCode: z.string().min(1).optional(),
            street: z.string().min(1).optional(),
        })
        .optional(),
    firstName: z.string().min(1).optional(),
    gender: z.nativeEnum(GENDERS).optional(),
    lastName: z.string().min(1).optional(),
    previousAddress: z
        .object({
            city: z.string().min(1).optional(),
            postalCode: z.string().min(1).optional(),
            street: z.string().min(1).optional(),
        })
        .optional(),
    schufaScore: z.string().optional(),
});

export type SchemaSchufaResponseSchufaKopf = z.infer<typeof schemaSchufaResponseSchufaKopf>;

export const schemaConsorsSchufaResponseEntry = z.object({
    decision: z.nativeEnum(SCHUFA_DECISION).optional(),
    identifier: z.string(),
    schufaInfo: z
        .object({
            schufaKopf: schemaSchufaResponseSchufaKopf.optional(),
            schufaPositionen: z.array(schemaSchufaResponsePosition).nullable().optional(),
            schufaState: z.nativeEnum(SCHUFA_STATE).optional(),
        })
        .optional(),
    status: z.nativeEnum(SCHUFA_STATUS).optional(),
});

export type SchemaConsorsSchufaResponseEntry = z.infer<typeof schemaConsorsSchufaResponseEntry>;
export const schemaConsorsSchufaResponse = z.object({
    preCheckRequestList: z.array(schemaConsorsSchufaResponseEntry).min(1),
});

export type SchemaConsorsSchufaResponse = z.infer<typeof schemaConsorsSchufaResponse>;

export const schemaConsorsSchufaBareResponse = z.object({
    preCheckRequestList: z.array(z.unknown()).min(1),
});

export type SchemaConsorsSchufaBareResponse = z.infer<typeof schemaConsorsSchufaBareResponse>;
